import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, Check, Users, Percent, Info } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUpload from "@/components/ImageUpload";
import RecurrencePicker from "@/components/RecurrencePicker";
import SeriesPatternEditor from "@/components/SeriesPatternEditor";
import { money } from "@/lib/format";

const CATEGORIES = ["Circle", "Ceremony", "Workshop", "Meditation", "Retreat", "Sound Healing", "Class"];

export default function EventFormModal({ event, onClose, onSaved }) {
  const isEdit = !!event?.id;
  const [form, setForm] = useState({
    title: event?.title || "",
    category: event?.category || "Circle",
    kind: event?.kind || "event",
    broadcast_url: event?.broadcast_url || "",
    description: event?.description || "",
    image: event?.image || "",
    location_id: event?.location_id || "virtual",
    room: event?.room || "",
    date: event?.date || "",
    start_time: event?.start_time || "18:00",
    end_time: event?.end_time || "20:00",
    price: event?.price ?? 0,
    capacity: event?.capacity ?? 20,
    published: event?.published ?? true,
    access_level: event?.access_level || "public",
    is_private: event?.is_private || false,
    invite_scope: event?.invite_scope || "open",
    invite_group_ids: event?.invite_group_ids || [],
    invite_member_ids: event?.invite_member_ids || [],
    collaborators: (event?.collaborators || []).map((c) => ({ ...c })),
  });
  const [recurrence, setRecurrence] = useState(null);
  const [collabQ, setCollabQ] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: locs } = useQuery({ queryKey: ["enh-schedulable-locs"], queryFn: () => api.get("/enhancer/me/schedulable-locations").then((r) => r.data) });
  const locationOptions = [...(locs?.locations || []), { id: "virtual", name: "Online / Virtual", type: "virtual" }];
  const selectedLoc = locationOptions.find((l) => l.id === form.location_id);
  const isCenter = selectedLoc?.type === "center";

  const { data: rooms = [] } = useQuery({
    queryKey: ["enh-loc-rooms", form.location_id], enabled: isCenter,
    queryFn: () => api.get(`/enhancer/location-rooms?location_id=${form.location_id}`).then((r) => r.data),
  });

  const { data: collabResults = [] } = useQuery({
    queryKey: ["enh-collab-search", collabQ], enabled: collabQ.trim().length > 0,
    queryFn: () => api.get(`/enhancer/me/collaborator-search?q=${encodeURIComponent(collabQ.trim())}`).then((r) => r.data),
  });

  const addCollab = (c) => {
    if (form.collaborators.some((x) => x.enhancer_id === c.id)) return;
    setForm((f) => ({ ...f, collaborators: [...f.collaborators, { enhancer_id: c.id, name: c.name, pct: 0 }] }));
    setCollabQ("");
  };
  const setCollabPct = (id, pct) => setForm((f) => ({ ...f, collaborators: f.collaborators.map((c) => c.enhancer_id === id ? { ...c, pct: Math.max(0, parseFloat(pct) || 0) } : c) }));
  const removeCollab = (id) => setForm((f) => ({ ...f, collaborators: f.collaborators.filter((c) => c.enhancer_id !== id) }));

  // Revenue preview: Ecosystem 10% → Center 20% (if at a center) → enhancer pool.
  const gross = parseFloat(form.price) || 0;
  const eco = +(gross * 0.10).toFixed(2);
  const centerFee = isCenter ? +(gross * 0.20).toFixed(2) : 0;
  const pool = +(gross - eco - centerFee).toFixed(2);
  const collabTotalPct = form.collaborators.reduce((s, c) => s + (parseFloat(c.pct) || 0), 0);
  const primaryPct = Math.max(0, 100 - collabTotalPct);
  const overAllocated = collabTotalPct > 100;

  const save = async () => {
    if (!form.title.trim() || !form.date) return toast.error("Title and date are required");
    if (isCenter && !form.room) return toast.error("Choose a room at the center");
    if (overAllocated) return toast.error("Collaborator shares exceed 100% of the enhancer pool");
    setSaving(true);
    try {
      const payload = { ...form, recurrence };
      if (isEdit) {
        await api.put(`/enhancer/events/${event.id}`, payload);
        toast.success("Event updated");
      } else {
        const { data } = await api.post("/enhancer/events", payload);
        toast.success(data.occurrences_created > 1 ? `Created ${data.occurrences_created} events` : "Event created");
      }
      onSaved?.(); onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not save event"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="enh-event-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-4">{isEdit ? "Edit event" : "New event"}</h3>
        <div className="space-y-3">
          <div><label className="text-sm font-medium mb-1 block">Title</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="enh-event-title" className="eyw-input w-full" /></div>

          <div><label className="text-sm font-medium mb-1 block">Type</label>
            <div className="flex gap-2">
              {[["event", "Event"], ["class", "Class"], ["broadcast", "Live broadcast"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, kind: v })} data-testid={`enh-event-kind-${v}`}
                  className={`rounded-full px-3.5 py-1.5 text-xs ${form.kind === v ? "bg-[#144F75] text-white" : "border border-[#E5E0D8] text-[#4A5D6B]"}`}>{l}</button>
              ))}
            </div>
            {form.kind === "broadcast" && (
              <input value={form.broadcast_url} onChange={(e) => setForm({ ...form, broadcast_url: e.target.value })}
                placeholder="Live link — Zoom, YouTube Live, etc." data-testid="enh-event-broadcast-url" className="eyw-input w-full mt-2" />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="enh-event-category" className="eyw-input w-full">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><label className="text-sm font-medium mb-1 block">Location</label>
              <select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value, room: "" })} data-testid="enh-event-location" className="eyw-input w-full">
                {locationOptions.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select></div>
          </div>

          {isCenter && (
            <div><label className="text-sm font-medium mb-1 block">Room</label>
              <select value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} data-testid="enh-event-room" className="eyw-input w-full">
                <option value="">Choose room…</option>
                {rooms.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
              </select></div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Date</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="enh-event-date" className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium mb-1 block">Start</label><input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium mb-1 block">End</label><input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="eyw-input w-full" /></div>
          </div>

          <div><label className="text-sm font-medium mb-1 block">Description</label>
            <RichTextEditor value={form.description} onChange={(html) => setForm({ ...form, description: html })} testid="enh-event-description" enableBlocks={false} placeholder="Describe the experience…" minHeight={140} />
          </div>
          <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} label="Event photo (square)" crop="square" testid="enh-event-image" />

          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Price ($)</label><input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} data-testid="enh-event-price" className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium mb-1 block">Capacity</label><input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })} className="eyw-input w-full" /></div>
          </div>

          <div><label className="text-sm font-medium mb-1 block">Access level</label>
            <select value={form.access_level} onChange={(e) => setForm({ ...form, access_level: e.target.value })} data-testid="enh-event-access" className="eyw-input w-full">
              <option value="public">Public — any guest</option>
              <option value="discovery">Discovery members only</option>
              <option value="community">Community members only</option>
              <option value="all_members">All members</option>
            </select>
          </div>

          {!isEdit ? (
            <div><label className="text-xs font-medium mb-1 block text-[#4A5D6B]">Repeat (optional)</label>
              <RecurrencePicker value={recurrence} onChange={setRecurrence} testid="enh-event-recurrence" startDate={form.date} />
            </div>
          ) : (
            <div><label className="text-xs font-medium mb-1 block text-[#4A5D6B]">Repeat pattern</label>
              <SeriesPatternEditor event={event} testid="enh-series-pattern" />
            </div>
          )}

          {/* Collaborators + revenue split */}
          <div className="rounded-xl border border-[#5C8A72]/30 bg-[#5C8A72]/6 p-3 space-y-2.5" data-testid="enh-event-collaborators">
            <p className="text-xs font-semibold text-[#5C8A72] flex items-center gap-1.5"><Users size={13} /> Co-facilitators & revenue split</p>
            <p className="text-[11px] text-[#4A5D6B] flex items-start gap-1"><Info size={12} className="mt-0.5 shrink-0" /> You're the primary facilitator. Invite other Enhancers and set each one's share of the <b>enhancer pool</b> — the ecosystem (10%){isCenter ? " and center (20%)" : ""} shares are never reduced.</p>

            {form.collaborators.map((c) => (
              <div key={c.enhancer_id} className="flex items-center gap-2" data-testid={`enh-event-collab-${c.enhancer_id}`}>
                <span className="flex-1 text-sm text-[#1A242B] truncate">{c.name}</span>
                <div className="relative">
                  <input type="number" value={c.pct} onChange={(e) => setCollabPct(c.enhancer_id, e.target.value)} data-testid={`enh-event-collab-pct-${c.enhancer_id}`} className="eyw-input !py-1 w-20 text-sm pr-6" />
                  <Percent size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
                </div>
                <button type="button" onClick={() => removeCollab(c.enhancer_id)} data-testid={`enh-event-collab-remove-${c.enhancer_id}`} className="text-red-500"><X size={15} /></button>
              </div>
            ))}

            <div className="relative">
              <input value={collabQ} onChange={(e) => setCollabQ(e.target.value)} placeholder="Search Enhancers to add…" data-testid="enh-event-collab-search" className="eyw-input w-full !py-1.5 text-sm" />
              {collabResults.length > 0 && (
                <div className="mt-1 rounded-lg border border-[#E5E0D8] bg-white divide-y divide-[#F0EDE6] max-h-40 overflow-y-auto">
                  {collabResults.filter((r) => !form.collaborators.some((c) => c.enhancer_id === r.id)).map((r) => (
                    <button key={r.id} type="button" onClick={() => addCollab(r)} data-testid={`enh-event-collab-opt-${r.id}`} className="w-full text-left px-3 py-1.5 text-sm text-[#4A5D6B] hover:bg-[#5C8A72]/8">{r.name} <span className="text-[11px] text-[#8FA89B]">{r.role}</span></button>
                  ))}
                </div>
              )}
            </div>

            {gross > 0 && (
              <div className="rounded-lg bg-white border border-[#E5E0D8] p-2.5 text-[12px] text-[#4A5D6B] space-y-0.5" data-testid="enh-event-revenue-preview">
                <div className="flex justify-between"><span>Ecosystem (10%)</span><span>{money(eco)}</span></div>
                {isCenter && <div className="flex justify-between"><span>Center location fee (20%)</span><span>{money(centerFee)}</span></div>}
                <div className="flex justify-between font-medium text-[#1A242B]"><span>Enhancer pool</span><span>{money(pool)}</span></div>
                <div className="flex justify-between pl-3"><span>You (primary · {primaryPct.toFixed(0)}%)</span><span>${(pool * primaryPct / 100).toFixed(2)}</span></div>
                {form.collaborators.map((c) => (
                  <div key={c.enhancer_id} className="flex justify-between pl-3"><span>{c.name} ({(parseFloat(c.pct) || 0).toFixed(0)}%)</span><span>${(pool * (parseFloat(c.pct) || 0) / 100).toFixed(2)}</span></div>
                ))}
                {overAllocated && <p className="text-red-600 font-medium pt-1">Shares exceed 100% of the pool — reduce a collaborator's %.</p>}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-[#4A5D6B]"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="enh-event-published" /> Published</label>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="enh-event-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} Save</button>
        </div>
      </div>
    </div>
  );
}
